import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import { toast } from 'sonner';
import {
	IContentCalendarSelectedSuggestion,
	IImpersonationContextComponentProps,
	IModalContext,
	ImpersonationContextKey,
	IMultiCampaignSchedulerResult,
	ModalChildComponentContextKey,
} from '../../../../../../models';
import { CampaignType } from '../../../../../../models/AdminModels';
import { shouldShowDuplicateTemplateWarning } from '../../../../../../models/Campaigns';
import { useEnvironment, useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import {
	accountHasComplianceEnabled,
	getSocialMediaPlainTextContentFromRawRichContent,
	getUniqueIdForSuggestion,
} from '../../../../../../models/UiUtils';
import { hasContentPreferences } from '../../../../../../utils/hasContentPreferences';
import {
	useAccountProfileQuery,
	useConvertSocialVideoMutation,
	useTemplateQueries,
	useUserQuery,
} from '../../../../../../queries';
import { AiButton } from '../../../../../components-v2/AiButton';
import { Collapsible, CollapsiblePanel } from '../../../../../components-v2/Collapsible';
import { AdvancedOptionsButton } from '../../../../../components/automation/AutomationStepCard/presentation';
import { Button } from '../../../../../components/Button';
import { useCampaignCalendarSuggestionsContext } from '../../../../../components/campaigns/CampaignCalendar/context';
import { ComplianceApprovalPrompt } from '../../../../../components/campaigns/ComplianceApprovalPrompt';
import { ISelectOption } from '../../../../../components/DeprecatedSelect';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { PrivateRoute } from '../../../../../components/PrivateRoute';
import { InfoIcon } from '../../../../../components/svgs/icons/InfoIcon';
import { getTemplateApprovalStatus } from '../../../../../components/TemplateReviewStatusIndicator';
import { PopoverType, TinyPopover } from '../../../../../components/TinyPopover';
import { Toggle } from '../../../../../components/Toggle';
import { charmGray } from '../../../../../styles/colors';
import { bs } from '../../../../../styles/styles';
import { useHandleAiGeneration } from '../../../hooks/handleAiGeneration';
import { WarningBanner, WarningBannerMessage } from '../../../presentation';
import { MultiSchedulerAiModal } from '../../aiModal';
import { MultiCampaignSchedulerLayout } from '../../MultiCampaignSchedulerLayout';
import { MultiCampaignTemplateItem } from '../../MultiCampaignTemplateItem';
import { useScheduleSocialMediaSuggestions, useSocialMediaTargetOptions } from './hooks';
import { ResetAllEditedContentModal } from './modals/ResetAllEditedContentModal';
import { ResetSuggestionEditedContentModal } from './modals/ResetSuggestionEditedContentModal';
import {
	EditableSocialMediaTemplateItem,
	MultiCampaignSocialMediaConnection,
	SocialMediaPostPreviewSelector,
	SocialMediaTemplateItem,
} from './presentation';
import { styleSheet } from './styles';

type SendFromOptions = {
	sendFrom: Api.ISendEmailFrom;
	sendFromUserId: string | null;
};

interface MultiSocialSchedulerProps extends IImpersonationContextComponentProps, IModalContext {
	styles?: StyleDeclarationValue[];
}

function _MultiSocialScheduler({ styles = [], parentModal, impersonationContext }: MultiSocialSchedulerProps) {
	const { url } = useRouteMatch();
	const history = useHistory();
	const location = useLocation();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const isAdminView = useEnvironment()?.appType === 'admin';
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const [editedVideoJobs, setEditedVideoJobs] = React.useState<Record<string, string>>({});
	const [isResetEditedContentModalOpen, setIsResetEditedContentModalOpen] = React.useState(false);
	const [suggestionIdForReset, setSuggestionIdForReset] = React.useState<string | null>(null);

	const { logApiError } = useEventLogging('MultiSocialScheduler');
	const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(false);
	const account = impersonationContext?.isValid ? impersonationContext.account : userSession.account?.toJs();
	const [overlayLogo, setOverlayLogo] = React.useState(() => {
		return account?.features.socialMedia.enabled && account?.features.socialMedia.alwaysOverlayLogo;
	});

	const hideWhiteBackground = React.useMemo(() => {
		return account?.features.socialMedia.enabled && account?.features.socialMedia.hideWhiteBackground;
	}, [account?.features.socialMedia.enabled, account?.features.socialMedia.hideWhiteBackground]);
	const [generatedContent, setGeneratedContent] = React.useState<Record<string, Api.IRawRichTextContentState>>({});
	const [generatedAiReferences, setGeneratedAiReferences] = React.useState<Record<string, Api.IAIReference>>({});

	const { pendingItems, queueItemForAiGeneration, isGenerating, processQueue, suggestionsWithErrors } =
		useHandleAiGeneration({
			contentType: 'social',
			impersonationContext,
			onAiGenerationComplete: (
				suggestion: IContentCalendarSelectedSuggestion,
				toolCall: Api.SocialMediaToolCall,
				conversationId: string
			) => {
				const key = getUniqueIdForSuggestion(suggestion);
				setGeneratedContent(prev => ({ ...prev, [key]: toolCall.content }));
				setGeneratedAiReferences(prev => ({ ...prev, [key]: { sessionId: conversationId } }));
			},
		});
	const videoPreviewRef = React.useRef<HTMLVideoElement | null>(null);

	const [showingOriginalContent, setShowingOriginalContent] = React.useState<string[]>([]);

	// State for editing suggestions
	const [editingSuggestionId, setEditingSuggestionId] = React.useState<string | null>(null);
	const [editedContent, setEditedContent] = React.useState<Record<string, Api.IRawRichTextContentState>>({});
	const [editedImages, setEditedImages] = React.useState<Record<string, Api.IFileAttachment[]>>({});

	const {
		selectedSuggestions,
		deselectSuggestion,
		updateStartDateForSuggestion,
		getStartDateForSuggestion,
		onScheduleSuccess,
	} = useCampaignCalendarSuggestionsContext();

	const { mutate: convertVideoMutation } = useConvertSocialVideoMutation();

	const [sendFromOptions, setSendFromOptions] = React.useState<SendFromOptions>(() => {
		let sendFrom: Api.ISendEmailFrom;
		let sendFromUserId: string = null;
		if (impersonationContext?.isValid) {
			sendFrom = Api.SendEmailFrom.SelectedUser;
			sendFromUserId = impersonationContext.user?.id || null;
		} else {
			sendFrom = Api.SendEmailFrom.CurrentUser;
			sendFromUserId = userSession.user.id;
		}

		return {
			sendFrom,
			sendFromUserId,
		};
	});

	const authorId =
		sendFromOptions.sendFrom === Api.SendEmailFrom.SelectedUser ? sendFromOptions.sendFromUserId : userSession.user.id;
	const userQuery = useUserQuery({
		id: authorId,
		enabled: Boolean(authorId),
		impersonationContext: impersonationContext?.toJs(),
	});
	const selectedUser = userQuery.data;

	const templateQueries = useTemplateQueries({
		impersonationContext: impersonationContext?.toJs(),
		templateIds: selectedSuggestions.map((t: IContentCalendarSelectedSuggestion) => t.templateReference.templateId),
		refetchOnWindowFocus: false,
		queryKeyProvider: (_, index) => {
			const suggestion = selectedSuggestions[index];
			return getUniqueIdForSuggestion(suggestion);
		},
	});
	const scheduledPostsRef = React.useRef<Map<string, Api.ISocialMediaPost>>(new Map());
	const suggestionPostErrorsRef = React.useRef<Set<[IContentCalendarSelectedSuggestion, Api.IOperationResultNoValue]>>(
		new Set()
	);
	const scheduleMutation = useScheduleSocialMediaSuggestions({
		onSuccess: (results, params) => {
			results.forEach((result, i) => {
				const suggestion = params.suggestions[i];
				if (result.status === 'fulfilled') {
					const socialPost = result.value;
					scheduledPostsRef.current.set(getUniqueIdForSuggestion(suggestion), socialPost);

					if (editedVideoJobs[getUniqueIdForSuggestion(suggestion)]) {
						convertVideoMutation({
							jobId: editedVideoJobs[getUniqueIdForSuggestion(suggestion)],
							postId: socialPost.id,
							forUserId: authorId,
							impersonationContext,
						});
					}
				} else {
					const error = Api.asApiError(result.reason);
					const matchingSuggestion = selectedSuggestions.find(
						(s: IContentCalendarSelectedSuggestion) =>
							getUniqueIdForSuggestion(suggestion) === getUniqueIdForSuggestion(s)
					);
					suggestionPostErrorsRef.current.add([matchingSuggestion, error]);
				}
			});

			if (scheduledPostsRef.current.size === selectedSuggestions.length) {
				const scheduled = Array.from(scheduledPostsRef.current?.values()) || [];
				const scheduleResult: IMultiCampaignSchedulerResult = {
					scheduledSuggestions: selectedSuggestions,
					withCompliance: params.sendWithCompliance,
				};

				toast.success(`${scheduled.length} social posts scheduled.`);

				const event = onScheduleSuccess(scheduleResult);
				if (!event.defaultPrevented) {
					parentModal.onRequestClose();
				}
			} else {
				toast.error(
					'An error occurred while scheduling some of the posts. If you retry we will only schedule the posts that were unsuccessfully scheduled.'
				);
			}
		},
		onError: (error: Api.IOperationResultNoValue) => {
			logApiError('MultiCampaignSocialScheduler-Error', error as Api.IOperationResultNoValue);
			errorMessages.pushApiError(error as Api.IOperationResultNoValue);
			toast.error('An error occurred while scheduling the posts.');
		},
	});

	const { facebookOptions, instagramOptions, linkedInOptions } = useSocialMediaTargetOptions({ user: selectedUser });
	const socialMediaOptions: {
		options: ISelectOption<Api.ISocialMediaConnection>[];
		provider: Api.SocialMediaType;
	}[] = React.useMemo(() => {
		return [
			{ options: facebookOptions, provider: Api.SocialMediaType.Facebook },
			{ options: instagramOptions, provider: Api.SocialMediaType.Instagram },
			{ options: linkedInOptions, provider: Api.SocialMediaType.LinkedIn },
		];
	}, [facebookOptions, instagramOptions, linkedInOptions]);

	React.useEffect(() => {
		if (!selectedUser) {
			return;
		}
		/**
		 * @If the user has a connected account, select the first connected account for each provider
		 * This is to ensure that the user doesn't have to manually select the connected account
		 */
		let newConnectionOptions: ISelectOption<Api.ISocialMediaConnection>[] = [];
		socialMediaOptions.forEach(({ options }) => {
			const connectedAccounts = options.filter(
				option => option.dataContext?.state === Api.SocialMediaConnectionState.Connected
			);
			if (options.length > 0 && connectedAccounts.length) {
				newConnectionOptions = newConnectionOptions.concat(connectedAccounts);
			}
		});
		setSelectedConnectionOptions(newConnectionOptions);
	}, [selectedUser, socialMediaOptions, sendFromOptions]);

	const [selectedConnectionOptions, setSelectedConnectionOptions] = React.useState<
		ISelectOption<Api.ISocialMediaConnection>[]
	>([]);

	const hasSelectedDisconnectedConnection = selectedConnectionOptions.some(option => {
		return option?.dataContext?.state !== Api.SocialMediaConnectionState.Connected;
	});

	const suggestionsWithoutDatesCount = selectedSuggestions.filter(
		(s: IContentCalendarSelectedSuggestion) => !getStartDateForSuggestion(s)
	).length;

	// Handlers for editing
	const handleEdit = (suggestion: IContentCalendarSelectedSuggestion) => {
		const id = getUniqueIdForSuggestion(suggestion);
		setEditingSuggestionId(id);
		// Initialize edited content/images if not already set
		if (!editedContent[id]) {
			const template = templateQueries.find((q, idx) => selectedSuggestions[idx] === suggestion)?.data;
			if (template) {
				// Prioritize generated content over template content if it exists
				const contentToUse = generatedContent[id] || template.content;
				setEditedContent(prev => ({ ...prev, [id]: contentToUse }));
				setEditedImages(prev => ({ ...prev, [id]: template.attachments || [] }));
			}
		}
	};

	const handleCancelEdit = (suggestion: IContentCalendarSelectedSuggestion) => {
		const id = getUniqueIdForSuggestion(suggestion);
		setEditingSuggestionId(null);
		// Optionally clear edited content/images
		setEditedContent(prev => {
			const newState = { ...prev };
			delete newState[id];
			return newState;
		});
		setEditedImages(prev => {
			const newState = { ...prev };
			delete newState[id];
			return newState;
		});
	};

	const handleSaveEdit = (_suggestion: IContentCalendarSelectedSuggestion) => {
		// The edited content/images are already stored in state
		// They will be used when rendering the template
		setEditingSuggestionId(null);
	};

	const handleContentChange = (
		suggestion: IContentCalendarSelectedSuggestion,
		content: Api.IRawRichTextContentState
	) => {
		const id = getUniqueIdForSuggestion(suggestion);
		setEditedContent(prev => ({ ...prev, [id]: content }));
	};

	const handleImageSelected = (image: Api.IFileAttachment) => {
		if (!editingSuggestionId) return;
		setEditedImages(prev => ({ ...prev, [editingSuggestionId]: image ? [image] : [] }));
	};

	const handleVideoSelected = (video: HTMLVideoElement, jobId: string) => {
		videoPreviewRef.current = video;
		setEditedVideoJobs(prev => ({ ...prev, [editingSuggestionId]: jobId }));
	};

	const scheduleSuggestions = (sendWithCompliance = false, sendWithComplianceEmail?: string) => {
		// Filter out suggestions that have already been successfully scheduled
		const unscheduledSuggestionsAndTemplates = selectedSuggestions
			.map((suggestion: IContentCalendarSelectedSuggestion, i: number) => ({
				suggestion,
				templateQuery: templateQueries[i],
			}))
			.filter(
				({
					suggestion,
					templateQuery,
				}: {
					suggestion: IContentCalendarSelectedSuggestion;
					templateQuery: ReturnType<typeof useTemplateQueries>[number];
				}) => {
					const id = getUniqueIdForSuggestion(suggestion);
					// Only include if not already scheduled and template data is available
					return !scheduledPostsRef.current.has(id) && templateQuery?.data;
				}
			);

		const unscheduledSuggestions = unscheduledSuggestionsAndTemplates.map(
			({ suggestion }: { suggestion: IContentCalendarSelectedSuggestion }) => suggestion
		);
		const unscheduledTemplateQueries = unscheduledSuggestionsAndTemplates.map(
			({ templateQuery }: { templateQuery: ReturnType<typeof useTemplateQueries>[number] }) => templateQuery
		);

		// Use edited content/images if available, otherwise use template defaults
		const templatesWithEdits = unscheduledTemplateQueries.map(
			(t: ReturnType<typeof useTemplateQueries>[number], i: number) => {
				const suggestion = unscheduledSuggestions[i];
				const id = getUniqueIdForSuggestion(suggestion);
				const editedContentForSuggestion = editedContent[id];
				const editedImagesForSuggestion = editedImages[id];

				if (editedContentForSuggestion || editedImagesForSuggestion) {
					return {
						...t.data,
						content: editedContentForSuggestion || t.data.content,
						attachments: editedImagesForSuggestion || t.data.attachments,
					};
				}
				return t.data;
			}
		);

		scheduleMutation.mutate({
			impersonationContext: impersonationContext?.toJs(),
			forUserId: authorId,
			templates: templatesWithEdits,
			suggestions: unscheduledSuggestions.map((suggestion: IContentCalendarSelectedSuggestion) => {
				return {
					...suggestion,
					schedule: {
						startDate: getStartDateForSuggestion(suggestion).toISOString(),
					},
					overlayLogo,
				} as IContentCalendarSelectedSuggestion;
			}),
			generatedContent,
			generatedAiReferences,
			editedContent,
			sendWithCompliance,
			sendWithComplianceEmail,
			designatedTargets: selectedConnectionOptions
				.map(connectionOption => {
					const connection = connectionOption.dataContext;
					if (connection?.postTargetId && connection?.userId) {
						return {
							provider: connection.type,
							pageId: connection.postTargetId,
							userId: connection.userId,
						};
					}
					return null;
				})
				.filter(Boolean),
		});
	};

	const canSchedule: boolean =
		suggestionsWithoutDatesCount === 0 &&
		!hasSelectedDisconnectedConnection &&
		!scheduleMutation.isPending &&
		!templateQueries.some(q => q.isPending) &&
		Object.values(selectedConnectionOptions).some(
			option => option?.dataContext?.state === Api.SocialMediaConnectionState.Connected
		) &&
		Boolean(impersonationContext?.isValid ? impersonationContext.user?.id : true);

	return (
		<div className={css(styleSheet.container, ...styles)}>
			{hasSelectedDisconnectedConnection ? (
				<WarningBanner>
					{selectedConnectionOptions.map(selectedConnectionOption => {
						const connection = selectedConnectionOption.dataContext;
						const provider = connection.type;
						if (connection && connection.state !== Api.SocialMediaConnectionState.Connected) {
							// TODO: update this for other states (e.g. no target)?
							return (
								<WarningBannerMessage key={connection.postTargetId}>
									<span>{`${Api.SocialMediaType[provider]} account: `}</span>
									<strong>{`@${connection?.postTargetDisplayName || connection?.userName}`}</strong>
									{` was disconnected ${moment(connection.expiresAt).format(
										'MM/DD/YYYY'
									)}. Please reconnect the account to schedule content on this platform.`}
								</WarningBannerMessage>
							);
						}
						return null;
					})}
				</WarningBanner>
			) : null}
			{suggestionsWithoutDatesCount > 0 ? (
				<WarningBanner>
					<>
						<WarningBannerMessage>
							{`You have `}
							<strong>{suggestionsWithoutDatesCount}</strong>
							{` without a send date. Please select a date/time to continue scheduling these posts.`}
						</WarningBannerMessage>
					</>
				</WarningBanner>
			) : null}
			<MultiCampaignSchedulerLayout
				{...sendFromOptions}
				hideContactOwnerOption
				title='Schedule Social Media Campaigns'
				onSendFromChange={(option, user) => {
					impersonationContext?.updateUser({
						id: option.sender.mode === Api.SendEmailFrom.SelectedUser ? user.id : userSession.user.id,
					});
					setSendFromOptions({ sendFrom: option.sender.mode, sendFromUserId: user?.id });
				}}
				styles={[styleSheet.scheduler]}
				isSocialMediaLayout
				sideBar={
					<Switch>
						<PrivateRoute path={`${url}/compliance-options`} userSession={userSession}>
							<ComplianceApprovalPrompt
								disabled={!canSchedule}
								isLoading={scheduleMutation.isPending}
								campaignType={CampaignType.Social}
								onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
									e.preventDefault();
									scheduleSuggestions(true, sendWithComplianceEmail);
								}}
								onScheduleWithoutCompliance={() => scheduleSuggestions()}
							/>
						</PrivateRoute>
						<PrivateRoute userSession={userSession}>
							<SocialMediaPostPreviewSelector
								templateQueries={templateQueries}
								selectedSuggestions={selectedSuggestions}
								generatedContent={generatedContent}
								editedContent={editedContent}
								editedImages={editedImages}
								overlayLogo={overlayLogo}
								impersonationCtx={impersonationContext}
								hideWhiteBackground={hideWhiteBackground}
							/>
							<div className={css(bs.py4, bs.px4)}>
								{requiresCompliance ? (
									<Button
										className={css(bs.wFull)}
										onClick={() => {
											history.push({
												pathname: `${url}/compliance-options`,
												state: location.state,
											});
										}}
										label='Next: Compliance Options'
										disabled={!canSchedule}
									/>
								) : (
									<Button
										className={css(bs.wFull)}
										onClick={() => scheduleSuggestions(false)}
										disabled={!canSchedule || scheduleMutation.isPending}
										isLoading={scheduleMutation.isPending}
										label='Schedule on suggested dates'
									/>
								)}
								<div className={css(styleSheet.advancedOptions)}>
									<Collapsible open={advancedOptionsToggled} onOpenChange={setAdvancedOptionsToggled}>
										<AdvancedOptionsButton
											isCollapsed={!advancedOptionsToggled}
											onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
										/>
										<CollapsiblePanel>
											<div className={css(bs.flex, bs.flexCenter, bs.gap2)}>
												<Toggle
													id='logo-toggle-multi-scheduler'
													isOn={overlayLogo}
													text={!overlayLogo ? 'Overlay Logo Off' : 'Overlay Logo On'}
													onToggleCheckChanged={() => {
														setOverlayLogo(!overlayLogo);
													}}
													uncheckedColor='#B1B4B6'
												/>
												<TinyPopover
													align='end'
													anchor={<InfoIcon fillColor={charmGray} />}
													autoCloseOtherPopoversOnHover
													dismissOnOutsideAction
													anchorStyles={[styleSheet.infoPopoverToggle]}
													placement={['top', 'right']}
													toggleOnHover
													type={PopoverType.lightBlue}
												>
													<div className={css(styleSheet.popover)}>
														This will overlay your account&apos;s logo on the post&apos;s media at the moment the post
														gets posted. Click on the image preview to see the overlay.
													</div>
												</TinyPopover>
											</div>
										</CollapsiblePanel>
									</Collapsible>
								</div>
							</div>
						</PrivateRoute>
					</Switch>
				}
			>
				<div className={css(bs.flex, bs.gap4, styleSheet.socialMediaDropdowns)}>
					{socialMediaOptions.map(({ options, provider }) => (
						<MultiCampaignSocialMediaConnection
							options={options}
							selectedOptions={selectedConnectionOptions.filter(option => option.dataContext.type === provider)}
							provider={provider}
							onSelectionChanged={option => {
								const newSelectedConnectionOptions = [...selectedConnectionOptions];
								if (newSelectedConnectionOptions.includes(option)) {
									const index = newSelectedConnectionOptions.indexOf(option);
									newSelectedConnectionOptions.splice(index, 1);
								} else {
									newSelectedConnectionOptions.push(option);
								}
								setSelectedConnectionOptions(newSelectedConnectionOptions);
							}}
							clearSelection={() => {
								const newSelectedConnectionOptions = selectedConnectionOptions.filter(
									x => x.dataContext.type !== provider
								);
								setSelectedConnectionOptions(newSelectedConnectionOptions);
							}}
							key={`social-media-${provider}`}
						/>
					))}
				</div>

				{isAdminView && hasContentPreferences(accountProfileQuery.data) && (
					<div className={css(styleSheet.customizeAllContentContainer)}>
						<AiButton
							title='Make Content Unique with AI'
							onClick={() => {
								// Check if there's any edited content
								const hasEditedContent = Object.keys(editedContent).length > 0 || Object.keys(editedImages).length > 0;
								if (hasEditedContent) {
									setIsResetEditedContentModalOpen(true);
								} else {
									selectedSuggestions.forEach((suggestion: IContentCalendarSelectedSuggestion) =>
										queueItemForAiGeneration(suggestion)
									);
									processQueue();
								}
							}}
							isLoading={isGenerating}
						/>
					</div>
				)}
				<div className={css(bs.flex, bs.flexCol, bs.px6, bs.pt4, bs.pb2, bs.gap8, bs.overflowAuto)}>
					{templateQueries.map((templateQuery, i) => {
						const suggestion = selectedSuggestions[i];
						if (!templateQuery.isSuccess) {
							return <LoadingSpinner key={`${getUniqueIdForSuggestion(suggestion)}-loading`} type='small' />;
						}
						const template = templateQuery.data;

						const scheduledDate = getStartDateForSuggestion(selectedSuggestions[i]);
						const id = getUniqueIdForSuggestion(suggestion);
						const generationFailed = suggestionsWithErrors.some(
							(s: IContentCalendarSelectedSuggestion) => id === getUniqueIdForSuggestion(s)
						);

						let templateWithGeneratedContent = null;
						const shouldShowOriginalContent = showingOriginalContent.includes(id);

						if (generatedContent[id] && !shouldShowOriginalContent) {
							templateWithGeneratedContent = { ...template, content: generatedContent[id] };
						}

						let schedulingState: 'scheduled' | 'failed' | null = null;

						if (scheduledPostsRef.current.has(id)) {
							schedulingState = 'scheduled';
						} else if (Array.from(suggestionPostErrorsRef.current || []).find(([s]) => s === suggestion)) {
							schedulingState = 'failed';
						}
						const isEditing = editingSuggestionId === id;
						// Priority: editedContent > generatedContent > template.content
						const editedTemplateContent = editedContent[id] || generatedContent[id] || template.content;
						const editedTemplateImages = editedImages[id] || template.attachments || [];
						// When not editing, prioritize editedContent over generatedContent
						const displayTemplate = isEditing
							? { ...template, content: editedTemplateContent, attachments: editedTemplateImages }
							: editedContent[id]
								? {
										...template,
										content: editedTemplateContent,
										attachments: editedTemplateImages,
									}
								: templateWithGeneratedContent || {
										...template,
										content: editedTemplateContent,
										attachments: editedTemplateImages,
									};

						return (
							<MultiCampaignTemplateItem
								onUpdateDate={(newDate: Date) => updateStartDateForSuggestion(suggestion, newDate)}
								key={getUniqueIdForSuggestion(suggestion)}
								name={
									<>
										{template?.name || 'Loading...'} {getTemplateApprovalStatus(template?.templateReview)}
									</>
								}
								isEditing={isEditing}
								scheduleDate={scheduledDate}
								onRemove={() => deselectSuggestion(suggestion)}
								error={Array.from(suggestionPostErrorsRef.current || []).find(([s]) => s === suggestion)?.[1]}
								{...sendFromOptions}
								postedOn={shouldShowDuplicateTemplateWarning(template) ? template?.lastUsedByDate?.account : undefined}
								isGeneratingContent={pendingItems.some(item => getUniqueIdForSuggestion(item) === id)}
								generationFailed={generationFailed}
								onEdit={() => handleEdit(suggestion)}
								onGenerateContent={() => {
									const suggestionId = getUniqueIdForSuggestion(suggestion);
									// Check if this specific suggestion has edited content
									const hasEditedContent = editedContent[suggestionId] || editedImages[suggestionId];
									if (hasEditedContent) {
										setSuggestionIdForReset(suggestionId);
									} else {
										queueItemForAiGeneration(suggestion);
										processQueue();
									}
								}}
								onShowOriginalContent={
									generatedContent[id]
										? () => {
												if (showingOriginalContent.includes(id)) {
													setShowingOriginalContent(prev => prev.filter(showingId => showingId !== id));
												} else {
													setShowingOriginalContent(prev => [...prev, id]);
												}
											}
										: undefined
								}
								showOriginalContent={showingOriginalContent.includes(id)}
								schedulingState={schedulingState}
							>
								{isEditing ? (
									<EditableSocialMediaTemplateItem
										template={displayTemplate}
										authorId={authorId}
										onContentChange={(content: Api.IRawRichTextContentState) =>
											handleContentChange(suggestion, content)
										}
										onImageSelected={handleImageSelected}
										onVideoSelected={handleVideoSelected}
										onSave={() => handleSaveEdit(suggestion)}
										onCancel={() => handleCancelEdit(suggestion)}
										impersonationContext={impersonationContext}
										postContent={getSocialMediaPlainTextContentFromRawRichContent(editedTemplateContent)}
									/>
								) : (
									<SocialMediaTemplateItem
										key={`socialMedia-template-${template.id}`}
										template={displayTemplate}
										isGeneratingContent={pendingItems.some(item => getUniqueIdForSuggestion(item) === id)}
										videoElement={videoPreviewRef.current}
									/>
								)}
							</MultiCampaignTemplateItem>
						);
					})}
				</div>
			</MultiCampaignSchedulerLayout>

			<MultiSchedulerAiModal
				onGenerateContentClicked={() => {
					selectedSuggestions.forEach((suggestion: IContentCalendarSelectedSuggestion) =>
						queueItemForAiGeneration(suggestion)
					);
					processQueue();
				}}
				impersonationContext={impersonationContext}
			/>

			<ResetAllEditedContentModal
				isOpen={isResetEditedContentModalOpen}
				onRequestClose={() => setIsResetEditedContentModalOpen(false)}
				onConfirm={() => {
					// Reset edited content and images
					setEditedContent({});
					setEditedImages({});
					setEditingSuggestionId(null);
					setIsResetEditedContentModalOpen(false);
					// Proceed with AI generation
					selectedSuggestions.forEach((suggestion: IContentCalendarSelectedSuggestion) =>
						queueItemForAiGeneration(suggestion)
					);
					processQueue();
				}}
			/>

			<ResetSuggestionEditedContentModal
				isOpen={suggestionIdForReset !== null}
				onRequestClose={() => setSuggestionIdForReset(null)}
				onConfirm={() => {
					if (suggestionIdForReset) {
						// Reset only this specific suggestion's edited content and images
						setEditedContent(prev => {
							const newState = { ...prev };
							delete newState[suggestionIdForReset];
							return newState;
						});
						setEditedImages(prev => {
							const newState = { ...prev };
							delete newState[suggestionIdForReset];
							return newState;
						});
						// If this suggestion is being edited, stop editing
						if (editingSuggestionId === suggestionIdForReset) {
							setEditingSuggestionId(null);
						}
						// Find the suggestion and proceed with AI generation
						const suggestion = selectedSuggestions.find(
							(s: IContentCalendarSelectedSuggestion) => getUniqueIdForSuggestion(s) === suggestionIdForReset
						);
						if (suggestion) {
							queueItemForAiGeneration(suggestion);
							processQueue();
						}
						setSuggestionIdForReset(null);
					}
				}}
			/>
		</div>
	);
}

export const MultiSocialScheduler = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(observer(_MultiSocialScheduler));
